import { AlertTriangle, ShieldX, Clock, ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useAccountRestrictions } from '@/hooks/use-account-restrictions';
import { useCustomerPortal } from '@/app/billing/hooks/use-billing-mutations';
import { ORG_ROUTES, PUBLIC_ROUTES } from '@/config/router';
import { brandingConfig } from '@/config/branding';

export default function RestrictedPage() {
    const navigate = useNavigate();
    const { orgSlug } = useParams<{ orgSlug: string }>();
    const { isAccountManager } = useAccountManager();
    const { hasAccessRestriction, restrictionReason, isLoading, noAccountLinked } = useAccountRestrictions();
    const portalMutation = useCustomerPortal();

    if (isAccountManager) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center p-4">
                <Card className="max-w-lg w-full">
                    <CardContent className="pt-6 text-center space-y-4">
                        <ShieldX className="mx-auto h-12 w-12 text-blue-500" />
                        <h1 className="text-xl font-semibold">Account Manager Access</h1>
                        <p className="text-sm text-muted-foreground">
                            This account has restrictions active. As an account manager, you have unrestricted access.
                        </p>
                        <Button
                            onClick={() => navigate(orgSlug ? ORG_ROUTES(orgSlug).ANALYTICS() : '/')}
                        >
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Return to Dashboard
                        </Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isLoading) {
        return null;
    }

    if (noAccountLinked) {
        return <Navigate to={PUBLIC_ROUTES.NO_ORGANIZATION} replace />;
    }

    if (!hasAccessRestriction) {
        return <Navigate to={orgSlug ? ORG_ROUTES(orgSlug).ANALYTICS() : '/'} replace />;
    }

    if (restrictionReason === 'unpaid_invoice') {
        return (
            <div className="flex min-h-[60vh] items-center justify-center p-4">
                <Card className="max-w-xl w-full border-red-200">
                    <CardContent className="pt-6 space-y-6">
                        <div className="text-center space-y-3">
                            <AlertTriangle className="mx-auto h-14 w-14 text-red-500" />
                            <h1 className="text-2xl font-bold text-red-900">
                                Account temporarily blocked due to unpaid invoice(s)
                            </h1>
                        </div>

                        <div className="space-y-3 text-sm text-gray-700">
                            <p>
                                One or more invoices have remained unpaid for more than 24 hours.
                                Your account has been temporarily blocked and access is no longer available.
                            </p>

                            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                                <p className="font-semibold text-red-800 flex items-center gap-2">
                                    <Clock className="h-4 w-4" />
                                    The account removal process has started and will result in:
                                </p>
                                <ul className="list-disc list-inside text-red-700 space-y-1 ml-6">
                                    <li>Removal of all stores</li>
                                    <li>Removal of all funnels</li>
                                    <li>Removal of all tokenized payment methods</li>
                                </ul>
                            </div>

                            <p className="font-medium">
                                To cancel this process and restore access to your account, please pay all outstanding invoices within the next 48 hours.
                            </p>
                        </div>

                        <div className="flex flex-col items-center gap-3">
                            <Button
                                variant="destructive"
                                size="lg"
                                onClick={() => portalMutation.mutate({ returnUrl: window.location.href })}
                                disabled={portalMutation.isPending}
                            >
                                {portalMutation.isPending ? 'Opening Payment Portal...' : 'Pay Now to Restore Access'}
                            </Button>
                            {brandingConfig.supportEmail && (
                                <p className="text-xs text-muted-foreground">
                                    If you think this is an error, please contact us at{' '}
                                    <a href={`mailto:${brandingConfig.supportEmail}`} className="text-primary hover:underline">{brandingConfig.supportEmail}</a>
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (restrictionReason === 'compliance_review') {
        return (
            <div className="flex min-h-[60vh] items-center justify-center p-4">
                <Card className="max-w-lg w-full">
                    <CardContent className="pt-6 text-center space-y-4">
                        <ShieldX className="mx-auto h-12 w-12 text-amber-500" />
                        <h1 className="text-xl font-semibold">Account Under Review</h1>
                        <p className="text-sm text-muted-foreground">
                            Your account is currently under review for compliance purposes. Our team will contact you shortly.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (restrictionReason === 'account_suspended') {
        return (
            <div className="flex min-h-[60vh] items-center justify-center p-4">
                <Card className="max-w-lg w-full border-red-200">
                    <CardContent className="pt-6 text-center space-y-4">
                        <ShieldX className="mx-auto h-12 w-12 text-red-500" />
                        <h1 className="text-xl font-semibold text-red-900">Account Suspended</h1>
                        <p className="text-sm text-muted-foreground">
                            Your account has been suspended. Please contact customer support for assistance.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    // Default
    return (
        <div className="flex min-h-[60vh] items-center justify-center p-4">
            <Card className="max-w-lg w-full">
                <CardContent className="pt-6 text-center space-y-4">
                    <ShieldX className="mx-auto h-12 w-12 text-red-500" />
                    <h1 className="text-xl font-semibold">Access Restricted</h1>
                    <p className="text-sm text-muted-foreground">
                        Your access to this area has been restricted. Please contact support if you believe this is an error.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
